import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Phone } from "lucide-react";
import { BRAND } from "../../lib/brand";

const IMG = "/images/logo-full.webp";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.1,
      delay: 0.25 + i * 0.14,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-cream pt-[72px] md:pt-[88px]"
      data-testid="hero-section"
    >
      {/* Editorial meta line removed per client request */}

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10 pt-8 md:pt-16 pb-24 md:pb-16 grid md:grid-cols-12 gap-8 md:gap-12 items-center min-h-[calc(100svh-72px)]">
        {/* Left: typography */}
        <motion.div
          style={{ y: textY }}
          className="md:col-span-7 relative z-10"
        >
          <h1
            className="font-serif text-olive leading-[0.92] tracking-tightest text-[15vw] md:text-[9.2vw] lg:text-[8.6vw]"
            data-testid="hero-headline"
          >
            {["Un instant", "hors du temps,", "au service", "de votre corps."].map((l, i) => (
              <span className="reveal-line" key={i}>
                <motion.span
                  variants={line}
                  initial="hidden"
                  animate="show"
                  custom={i}
                  className={i === 2 ? "text-gold" : ""}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="mt-8 md:mt-10 max-w-[520px] text-olive/75 text-base md:text-[17px] leading-[1.65] font-light"
          >
            Maderothérapie & drainage lymphatique — pour retrouver légèreté,
            tonicité et bien-être.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <a
              href={BRAND.phoneHref}
              data-testid="cta-hero-reserve"
              className="btn-gold inline-flex items-center gap-3 px-8 py-4 text-[12px] uppercase tracking-[0.24em] rounded-full font-medium"
            >
              <Phone className="h-4 w-4" strokeWidth={1.6} />
              Je réserve mon soin
            </a>
          </motion.div>
        </motion.div>

        {/* Right: brand logo — centré mobile, contenu desktop */}
        <div className="md:col-span-5 relative flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full flex items-center justify-center"
          >
            <motion.img
              src={IMG}
              alt="Corps & Sens — logo"
              style={{ y, scale }}
              className="mx-auto w-[85%] sm:w-[75%] md:w-[110%] lg:w-[115%] h-auto max-w-[720px] object-contain select-none pointer-events-none"
              loading="eager"
              draggable="false"
            />
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-olive/60"
      >
        <span className="text-[10px] uppercase tracking-[0.32em]">
          Défiler
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="h-4 w-4" strokeWidth={1.3} />
        </motion.div>
      </motion.div>
    </section>
  );
}
